import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../../../../backend/convex/_generated/api';
import type { Doc } from '../../../../../backend/convex/_generated/dataModel';
import { WorkspaceAdminLayout } from '../../components/WorkspaceAdminLayout';
import { useWorkspace } from '../../hooks/useWorkspace';
import { AlertModal } from '../../components/Modal';
import { ConfirmModal } from '../../components/ConfirmModal';
import { useAlert, useConfirm } from '../../hooks/useAdminUI';

export const AssignmentManagement: React.FC = () => {
  const { workspace } = useWorkspace();
  const assessments = useQuery(api.assessments.list, 
    workspace ? { workspaceId: workspace._id } : "skip"
  );
  const members = useQuery(api.members.listWorkspaceMembers, 
    workspace ? { workspaceId: workspace._id } : "skip"
  );

  useEffect(() => {
    document.title = 'LibreRank - Assignments';
  }, []);

  const [selectedAssessmentId, setSelectedAssessmentId] = useState<any>(null);
  const assignments = useQuery(api.assignments.listByAssessment, 
    selectedAssessmentId ? { assessmentId: selectedAssessmentId } : "skip"
  );

  const [viewingSubmissionsUser, setViewingSubmissionsUser] = useState<string | null>(null);
  const submissions = useQuery(api.submissions.listSubmissions, 
    workspace && viewingSubmissionsUser && selectedAssessmentId 
      ? { workspaceId: workspace._id, userId: viewingSubmissionsUser, assessmentId: selectedAssessmentId } 
      : "skip"
  );

  const assignAssessment = useMutation(api.assignments.assign);
  const removeAssignment = useMutation(api.assignments.remove);

  // Modal state
  const { alert, showAlert, closeAlert } = useAlert();
  const { confirm: confirmRemove, showConfirm: showConfirmRemove, closeConfirm: closeConfirmRemove } = useConfirm<{ id: any; name: string }>();
  const { confirm: confirmBulk, showConfirm: showConfirmBulk, closeConfirm: closeConfirmBulk } = useConfirm<null>();

  const handleAssign = async (userId: string) => {
    if (!selectedAssessmentId) return;

    try {
      await assignAssessment({
        assessmentId: selectedAssessmentId,
        userId: userId,
      });
    } catch (err) {
      showAlert('Assignment Failed', err instanceof Error ? err.message : 'An unknown error occurred', 'error');
    }
  };

  const handleRemoveClick = (id: any, name: string) => {
    showConfirmRemove('Remove Assignment', { id, name });
  };

  const handleConfirmRemove = async () => {
    if (confirmRemove.data?.id) {
      try {
        await removeAssignment({ id: confirmRemove.data.id });
      } catch (err) {
        showAlert('Removal Failed', err instanceof Error ? err.message : 'An unknown error occurred', 'error');
      } finally {
        closeConfirmRemove();
      }
    }
  };

  const handleAssignAllClick = () => {
    if (!selectedAssessmentId || !members) return;
    showConfirmBulk('Bulk Assign', null);
  };

  const handleConfirmAssignAll = async () => {
    if (!selectedAssessmentId || !members) return;
    try {
      await Promise.all(members.map((member: Doc<"workspace_members"> & { user: Doc<"users"> | null }) => 
        member.user ? assignAssessment({
          assessmentId: selectedAssessmentId,
          userId: member.user.email,
        }) : Promise.resolve()
      ));
      showAlert('Bulk Assignment Complete', `Successfully assigned assessment to ${members.length} members.`, 'success');
    } catch (err) {
      showAlert('Bulk Assignment Failed', err instanceof Error ? err.message : 'An unknown error occurred', 'error');
    } finally {
      closeConfirmBulk();
    }
  };

  const getMemberName = (userId: string) => {
    const member = members?.find(m => m.user?.tokenIdentifier === userId || m.user?.email === userId);
    return member?.user?.name || member?.user?.email || 'Unknown';
  };

  return (
    <WorkspaceAdminLayout title="Assignment Management">
      <AlertModal 
        isOpen={alert.isOpen} 
        onClose={closeAlert} 
        title={alert.title} 
        message={alert.message} 
        type={alert.type} 
      />
      
      <ConfirmModal 
        isOpen={confirmRemove.isOpen}
        title="Remove Assignment"
        message={`Are you sure you want to remove the assignment for ${confirmRemove.data?.name}? This will remove their access to the assessment.`}
        confirmText="Remove"
        confirmVariant="destructive"
        onConfirm={handleConfirmRemove}
        onCancel={closeConfirmRemove}
      />

      <ConfirmModal 
        isOpen={confirmBulk.isOpen}
        title="Bulk Assign"
        message={`Are you sure you want to assign this assessment to all ${members?.length || 0} workspace members?`}
        confirmText="Assign All"
        confirmVariant="success"
        onConfirm={handleConfirmAssignAll}
        onCancel={closeConfirmBulk}
      />

      <div className="flex flex-col gap-6">
        <section className="p-6 border border-border rounded-xl bg-card shadow-sm">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Select Assessment</h3>
          </div>
          <select 
            value={selectedAssessmentId || ''} 
            onChange={e => {
              setSelectedAssessmentId(e.target.value as any);
              setViewingSubmissionsUser(null);
            }}
            className="w-full p-3 rounded-lg border border-border bg-background text-foreground focus:ring-2 focus:ring-primary/20 transition-all outline-none"
          >
            <option value="">-- Choose an assessment to manage assignments --</option>
            {assessments?.map((a: Doc<"assessments">) => (
              <option key={a._id} value={a._id}>{a.title}</option>
            ))}
          </select>
        </section>

        {selectedAssessmentId && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Members List */}
            <section className="p-6 border border-border rounded-xl bg-card shadow-sm flex flex-col h-[600px]">
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-lg font-semibold">Workspace Members</h3>
                <button 
                  onClick={handleAssignAllClick}
                  className="text-xs font-semibold px-3 py-1.5 bg-secondary text-secondary-foreground border border-border rounded-md hover:bg-secondary/80 transition-colors"
                >
                  Assign to All
                </button>
              </div>
              <p className="text-sm text-muted-foreground mb-6">
                Assign this assessment to members of your workspace.
              </p>
              
              <div className="flex-1 overflow-y-auto pr-2 space-y-2">
                {!members ? (
                  <div className="py-8 text-center text-muted-foreground italic">Loading members...</div>
                ) : members.length === 0 ? (
                  <div className="py-8 text-center text-muted-foreground italic border border-dashed border-border rounded-lg">No members found.</div>
                ) : (
                  members.map((member: Doc<"workspace_members"> & { user: Doc<"users"> | null }) => {
                    const isAssigned = assignments?.some((a: Doc<"assessment_assignments">) => a.userId === member.user?.email);
                    return (
                      <div key={member._id} className={`flex justify-between items-center p-4 border rounded-xl transition-all ${isAssigned ? 'bg-muted/50 border-transparent' : 'bg-background border-border hover:border-primary/30'}`}>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-xs uppercase">
                            {(member.user?.name || member.user?.email || '?')[0]}
                          </div>
                          <div>
                            <div className="font-semibold text-sm">{member.user?.name || member.user?.email}</div>
                            <div className="text-xs text-muted-foreground capitalize">{member.role}</div>
                          </div>
                        </div>
                        <button 
                          onClick={() => member.user && handleAssign(member.user.email)}
                          disabled={isAssigned || !member.user}
                          className={`px-4 py-1.5 text-xs font-bold rounded-lg transition-all ${
                            isAssigned 
                              ? 'bg-success/10 text-success cursor-default' 
                              : 'bg-primary text-primary-foreground hover:bg-primary/90'
                          }`}
                        >
                          {isAssigned ? '✓ Assigned' : 'Assign'}
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            </section>

            {/* Current Assignments & Monitoring */}
            <section className="p-6 border border-border rounded-xl bg-card shadow-sm flex flex-col h-[600px]">
              <h3 className="text-lg font-semibold mb-2">Active Assignments</h3>
              <p className="text-sm text-muted-foreground mb-6">
                Track progress and monitor submissions in real-time.
              </p>
              
              <div className="flex-1 overflow-y-auto pr-2 space-y-4">
                {!assignments ? (
                  <div className="py-8 text-center text-muted-foreground italic">Loading assignments...</div>
                ) : assignments.length === 0 ? (
                  <div className="py-12 text-center text-muted-foreground border border-dashed border-border rounded-xl">
                    <div className="text-3xl mb-2">⏳</div>
                    <p>No one is assigned to this assessment yet.</p>
                  </div>
                ) : (
                  assignments.map((assignment: Doc<"assessment_assignments">) => (
                    <div key={assignment._id} className={`border rounded-xl transition-all ${viewingSubmissionsUser === assignment.userId ? 'border-primary shadow-sm bg-primary/[0.02]' : 'border-border bg-background'}`}>
                      <div className="p-4 flex justify-between items-center">
                        <div className="flex items-center gap-3">
                          <div className="relative">
                            <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center text-lg">
                              👤
                            </div>
                            <div className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-background ${
                              assignment.status === 'completed' ? 'bg-success' : 
                              assignment.status === 'in_progress' ? 'bg-info animate-pulse' : 
                              'bg-muted-foreground'
                            }`}></div>
                          </div>
                          <div>
                            <div className="font-semibold text-sm">{getMemberName(assignment.userId)}</div>
                            <div className="text-[10px] uppercase font-bold tracking-wider flex items-center gap-1.5 mt-0.5">
                              <span className={
                                assignment.status === 'completed' ? 'text-success' : 
                                assignment.status === 'in_progress' ? 'text-info' : 
                                'text-muted-foreground'
                              }>
                                {assignment.status}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button 
                            onClick={() => setViewingSubmissionsUser(viewingSubmissionsUser === assignment.userId ? null : assignment.userId)}
                            className={`p-2 rounded-lg transition-colors ${
                              viewingSubmissionsUser === assignment.userId 
                                ? 'bg-primary text-primary-foreground' 
                                : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
                            }`}
                            title="Monitor Submissions"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>
                          </button>
                          <button 
                            onClick={() => handleRemoveClick(assignment._id, getMemberName(assignment.userId))}
                            className="p-2 text-destructive bg-destructive/10 rounded-lg hover:bg-destructive/20 transition-colors"
                            title="Remove Assignment"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/><line x1="10" x2="10" y1="11" y2="17"/><line x1="14" x2="14" y1="11" y2="17"/></svg>
                          </button>
                        </div>
                      </div>
                      
                      {viewingSubmissionsUser === assignment.userId && (
                        <div className="border-t border-border px-4 py-4 bg-muted/30 rounded-b-xl">
                          <div className="flex justify-between items-center mb-3">
                            <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Detailed Submissions</h4>
                            {submissions && submissions.length > 0 && (
                              <span className="text-[10px] bg-primary/10 text-primary px-2 py-0.5 rounded-full font-bold">
                                {submissions.length} total
                              </span>
                            )}
                          </div>
                          
                          {!submissions ? (
                            <div className="flex items-center justify-center py-4">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mr-2"></div>
                              <span className="text-xs text-muted-foreground italic">Fetching submissions...</span>
                            </div>
                          ) : submissions.length === 0 ? (
                            <div className="text-center py-6 bg-background/50 border border-dashed border-border rounded-lg text-xs text-muted-foreground italic">
                              No submissions yet.
                            </div>
                          ) : (
                            <div className="space-y-2">
                              {submissions.map((s: Doc<"submissions">) => (
                                <div key={s._id} className="bg-background p-3 rounded-lg border border-border flex justify-between items-center shadow-sm">
                                  <div className="flex items-center gap-3">
                                    <div className={`w-2 h-2 rounded-full ${s.verdict === 'Accepted' ? 'bg-success' : 'bg-destructive'}`}></div>
                                    <div>
                                      <div className="text-xs font-semibold">{s.problemId}</div>
                                      <div className="text-[10px] text-muted-foreground">{new Date(s.createdAt).toLocaleString()}</div>
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <div className={`text-xs font-bold ${s.verdict === 'Accepted' ? 'text-success' : 'text-destructive'}`}>
                                      {s.verdict || s.status}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </section>
          </div>
        )}
      </div>
    </WorkspaceAdminLayout>
  );
};

